/**
 * EP-X inside BB (Studio → EP-X, an iframe of ep.breakinbread.com) and the
 * line coπ talks to it on (Gordon 2026-11-01: "make an electro beat in EPX at
 * 118 bpm in C minor"). EP-X's embed protocol (its src/utils/embedHost.ts):
 *   BB → EP-X  { source: 'ep-x', type: 'cmd', name, payload }
 *   EP-X → BB  { source: 'ep-x', type: 'event', name, payload }
 * `copi` runs one of EP-X's coπ commands (src/copi/commands.ts) and answers
 * with a `copi-result` event carrying the same id; `copi-list` lists them.
 * Messages only ever go to, and are only taken from, EP-X's own origin.
 *
 * Memos (Gordon 2026-11-01: a memo recorded in BB can be called up in EP-X's
 * sampler with the same account): EP-X asks, BB answers —
 *   EP-X → BB  event 'memos-request' { id }            → BB cmd 'memos' { id, signedIn, memos: [{ id, title, type, duration, createdAt }] }
 *   EP-X → BB  event 'memo-request'  { id, memoId }    → BB cmd 'memo-audio' { id, memoId, title, mimeType, data: ArrayBuffer } (transferred)
 * BB fetches the audio itself (its storage only serves BB's own origins), so
 * EP-X never sees a storage URL and needs no login of its own.
 *
 * Sign in with BB (docs/copi-everywhere-plan.md §2): EP-X uses the BB account,
 * no second login. EP-X asks, BB answers with the person's Firebase ID token —
 * only ever to EP-X's origin, only to the EP-X frame this page shows:
 *   EP-X → BB  event 'bb-identity-request' { id }  → BB cmd 'bb-identity' { id, signedIn, uid?, idToken? } | { id, error }
 *   BB → EP-X  cmd 'bb-identity' { signedIn, uid?, idToken? } — pushed when the person signs in / out of BB
 * EP-X's Cloud Function bbSignIn verifies the token against Google's keys for
 * project breakin-bread-6rr1a and signs EP-X in with its own custom token. No
 * secret crosses between the projects.
 */

export const EPX_ORIGIN = 'https://ep.breakinbread.com';
export const EPX_EMBED_URL = `${EPX_ORIGIN}/?embed=1`;

type Result = { id: string; ok: boolean; result?: unknown; error?: string; code?: string };

let frame: HTMLIFrameElement | null = null;
let ready = false;
let readyWaiters: (() => void)[] = [];
const pending = new Map<string, { resolve: (r: Result) => void; timer: ReturnType<typeof setTimeout> }>();
let listening = false;
/** Set by the EP-X page: answers EP-X's memo requests for the signed-in person. */
let memoSource: { list: () => Promise<{ id: string; title: string; type: string; duration: number; createdAt: number; audioUrl: string }[]> | null } | null = null;
export function setEpxMemoSource(src: typeof memoSource) { memoSource = src; }
/** Who is signed in to BB, for EP-X's Sign in with BB. */
export type EpxIdentity = { signedIn: false } | { signedIn: true; uid: string; idToken: string };
/** Set by the EP-X page once BB knows who is signed in (null when it unmounts). */
let identitySource: (() => Promise<EpxIdentity>) | null = null;
let identityWaiters: (() => void)[] = [];
export function setEpxIdentitySource(src: typeof identitySource) {
  identitySource = src;
  if (src) { const w = identityWaiters; identityWaiters = []; w.forEach(f => f()); }
}
/** How long an identity request waits for the page to know who is signed in. */
const IDENTITY_WAIT_MS = 10_000;
/** Set by the EP-X page: EP-X asks the host for full screen (`fullscreen-request` { on }). */
let fullscreenHandler: ((on: boolean) => void) | null = null;
export function setEpxFullscreenHandler(fn: typeof fullscreenHandler) { fullscreenHandler = fn; }

function listen() {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  window.addEventListener('message', (e: MessageEvent) => {
    if (e.origin !== EPX_ORIGIN || !frame || e.source !== frame.contentWindow) return;
    const d = e.data as { source?: string; type?: string; name?: string; payload?: any } | null;
    if (!d || d.source !== 'ep-x' || d.type !== 'event') return;
    if (d.name === 'ready') {
      ready = true;
      const w = readyWaiters; readyWaiters = [];
      w.forEach(f => f());
    } else if (d.name === 'fullscreen-request') {
      fullscreenHandler?.(d.payload?.on !== false);
    } else if (d.name === 'memos-request' && typeof d.payload?.id === 'string') {
      void answerMemos(String(d.payload.id));
    } else if (d.name === 'memo-request' && typeof d.payload?.id === 'string' && typeof d.payload?.memoId === 'string') {
      void answerMemo(String(d.payload.id), String(d.payload.memoId));
    } else if (d.name === 'bb-identity-request' && typeof d.payload?.id === 'string' && d.payload.id.length <= 200) {
      void answerIdentity(String(d.payload.id), e.source as Window);
    } else if (d.name === 'copi-result' && d.payload && typeof d.payload.id === 'string') {
      const p = pending.get(d.payload.id);
      if (!p) return;
      clearTimeout(p.timer);
      pending.delete(d.payload.id);
      p.resolve(d.payload as Result);
    }
  });
}

/** The page that shows EP-X registers its iframe (null when it unmounts). */
export function setEpxFrame(el: HTMLIFrameElement | null) {
  listen();
  if (el !== frame) ready = false;
  frame = el;
}

export const epxMounted = () => !!frame;

/** Resolves once EP-X said it's ready (or after `ms`, whichever first). */
export function epxReady(ms = 20_000): Promise<boolean> {
  if (ready) return Promise.resolve(true);
  return new Promise(resolve => {
    const timer = setTimeout(() => resolve(false), ms);
    readyWaiters.push(() => { clearTimeout(timer); resolve(true); });
  });
}

function send(name: string, payload: unknown, transfer?: Transferable[]) {
  frame?.contentWindow?.postMessage({ source: 'ep-x', type: 'cmd', name, payload }, EPX_ORIGIN, transfer);
}

async function answerMemos(id: string) {
  const list = memoSource ? await memoSource.list()?.catch(() => null) : null;
  send('memos', {
    id, signedIn: !!list,
    memos: (list || []).map(({ id: memoId, title, type, duration, createdAt }) => ({ id: memoId, title, type, duration, createdAt })),
  });
}

async function answerMemo(id: string, memoId: string) {
  try {
    const memo = (memoSource ? await memoSource.list() : null)?.find(m => m.id === memoId);
    if (!memo) throw new Error('not found');
    const res = await fetch(memo.audioUrl);
    if (!res.ok) throw new Error(`audio ${res.status}`);
    const data = await res.arrayBuffer();
    send('memo-audio', { id, memoId, title: memo.title, mimeType: res.headers.get('content-type') || 'audio/webm', data }, [data]);
  } catch (e) {
    send('memo-audio', { id, memoId, error: (e as Error).message || 'unavailable' });
  }
}

async function currentIdentity(): Promise<EpxIdentity | null> {
  if (!identitySource) {
    await new Promise<void>(resolve => {
      const timer = setTimeout(resolve, IDENTITY_WAIT_MS);
      identityWaiters.push(() => { clearTimeout(timer); resolve(); });
    });
  }
  const src = identitySource;
  if (!src) return null;
  try { return await src(); } catch { return null; }
}

async function answerIdentity(id: string, asker: Window) {
  const identity = await currentIdentity();
  // Only to the EP-X frame that asked, if it is still the one on this page.
  if (!frame || frame.contentWindow !== asker) return;
  // Can't tell (no page, no network for a token): say so — EP-X then changes nothing.
  send('bb-identity', identity ? { id, ...identity } : { id, error: 'unavailable' });
}

/** The person signed in to / out of BB while EP-X is open: tell EP-X. */
export async function pushEpxIdentity() {
  if (!frame?.contentWindow) return;
  const identity = await currentIdentity();
  if (identity) send('bb-identity', identity);
}

/** One plain embed command (set-tab, enter-fullscreen …). */
export function epxCmd(name: string, payload?: unknown) { send(name, payload); }

// Loading a demo song or kit fetches megabytes (the hip-hop kit is 8 MB): give them time.
const SLOW = new Set(['beat.make', 'demo.load', 'kit.load', 'project.new', 'project.starter', 'memo.load']);

/** Run one EP-X coπ command and wait for its read-back. */
export function epxCommand(command: string, params?: Record<string, unknown>, ms?: number): Promise<Result> {
  if (ms === undefined) {
    const secs = Number((params as { seconds?: unknown } | undefined)?.seconds);
    ms = command === 'sampler.record' && Number.isFinite(secs) ? (secs + 20) * 1000 : SLOW.has(command) ? 90_000 : 15_000;
  }
  if (!frame?.contentWindow) return Promise.resolve({ id: '', ok: false, error: 'EP-X is not open', code: 'not_open' });
  const id = `bb_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  return new Promise(resolve => {
    const timer = setTimeout(() => { pending.delete(id); resolve({ id, ok: false, error: 'EP-X did not answer', code: 'timeout' }); }, ms);
    pending.set(id, { resolve, timer });
    send('copi', { id, command, params: params || {} });
  });
}

/** What EP-X can do — name, description, params — for coπ's planner. EP-X
 *  answers `not_ready` (or nothing) while it boots, so ask again for a while. */
export async function epxCommandList(): Promise<unknown[] | null> {
  for (let attempt = 0; attempt < 12; attempt++) {
    if (!frame?.contentWindow) return null;
    const id = `bb_list_${Date.now().toString(36)}_${attempt}`;
    const r = await new Promise<Result>(resolve => {
      const timer = setTimeout(() => { pending.delete(id); resolve({ id, ok: false, code: 'timeout' }); }, 4000);
      pending.set(id, { resolve, timer });
      send('copi-list', { id });
    });
    if (r.ok && Array.isArray(r.result)) return r.result;
    if (r.code && r.code !== 'not_ready' && r.code !== 'timeout') return null;
    await new Promise(res => setTimeout(res, 1000));
  }
  return null;
}
